import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@workspace/lib';
import { theme } from '../../theme';

// Converts ^text^ patterns in string children to <sup> elements.
// Unpaired ^ characters are left as-is. Non-string children pass through unchanged.
function processChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') {
    // Fast path: skip regex entirely when no ^ is present
    if (!children.includes('^')) return children;
    // Split on paired ^...^ — captured groups land at odd indices
    const parts = children.split(/\^([^^]+)\^/);
    if (parts.length === 1) return children;
    return parts.map((part, i) => (i % 2 === 1 ? <sup key={i}>{part}</sup> : part));
  }
  if (Array.isArray(children)) {
    return children.map((child, i) =>
      typeof child === 'string' ? <React.Fragment key={i}>{processChildren(child)}</React.Fragment> : child,
    );
  }
  return children;
}

export const typographyCva = cva('', {
  variants: {
    variant: {
      h1: 'font-heading text-4xl md:text-6xl leading-12 md:leading-19.5',
      h2: 'font-heading text-[28px] md:text-5xl leading-9 md:leading-15',
      h3: 'font-heading text-2xl md:text-4xl leading-7.5 md:leading-12',
      h4: 'font-heading text-2xl md:text-[28px] leading-7.5 md:leading-9',
      h5: 'font-heading font-semibold text-lg md:text-2xl leading-6 md:leading-7.5',
      h6: 'font-heading text-lg md:text-lg leading-6',
      bodyLg: 'font-body font-normal text-xl md:text-xl leading-6 md:leading-8',
      bodyMd: 'font-body font-normal text-base md:text-base leading-5 md:leading-6',
      bodySm: 'font-body font-normal text-sm md:text-sm leading-4  md:leading-5',
      bodyXs: 'font-body font-normal text-xs md:text-xs leading-4',
      bodyTiny: 'font-body font-normal text-tiny leading-3',
      link: 'font-body font-semibold text-sm underline cursor-pointer',
      button: 'font-body font-semibold text-base',
      hyperlinkVisited: 'font-body font-semibold text-sm underline cursor-pointer',
    },
    tone: {
      default: '',
      link: 'text-primary',
      linkVisited: 'text-primary-500',
    },
    site: {
      porta: '',
      portaNZ: '',
      redRec: '',
    },
    loading: {
      true: 'animate-pulse bg-secondary-50 text-transparent rounded-md',
      false: '',
    },
  },
  compoundVariants: [
    { site: 'porta', tone: 'default', className: 'text-secondary-400' },
    { site: 'portaNZ', tone: 'default', className: 'text-secondary-400' },
    {
      site: ['porta', 'portaNZ'],
      variant: ['h1', 'h2', 'h3', 'h4', 'h6'],
      className: 'font-bold',
    },
    {
      site: 'redRec',
      variant: ['h1', 'h2', 'h3', 'h4', 'h6'],
      className: 'font-semibold',
    },
  ],
  defaultVariants: { tone: 'default', loading: false },
});

type TVariants = VariantProps<typeof typographyCva>;
export type TypographyVariant = NonNullable<TVariants['variant']>;

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
  href?: string;
  as?: React.ElementType;
  onClick?: (e: React.MouseEvent) => void;
  fontWeight?: string;
  textColor?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  styleVariant?: TypographyVariant;
}

export function Typography({
  variant,
  children,
  className,
  href,
  as,
  onClick,
  fontWeight,
  textColor,
  style,
  loading = false,
  styleVariant,
  ...rest
}: TypographyProps) {
  const tone: TVariants['tone'] =
    variant === 'link' ? 'link' : variant === 'hyperlinkVisited' ? 'linkVisited' : 'default';

  // Use styleVariant for styling if provided, otherwise use variant
  const styleVariantToUse = styleVariant || variant;
  const classes = typographyCva({ variant: styleVariantToUse, tone, site: theme, loading });
  const merged = cn(classes, fontWeight, className);
  const commonProps = {
    onClick,
    className: merged,
    style: textColor ? { ...style, color: textColor } : style,
    href,
    ...rest,
  };

  const defaultTag: Record<TypographyVariant, React.ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    bodyLg: 'p',
    bodyMd: 'p',
    bodySm: 'p',
    bodyXs: 'p',
    bodyTiny: 'p',
    link: href ? 'a' : 'span',
    hyperlinkVisited: href ? 'a' : 'span',
    button: 'span',
  };

  const Component = as ?? defaultTag[variant] ?? 'span';

  const processedChildren = processChildren(children);

  return <Component {...commonProps}>{processedChildren}</Component>;
}
